import React, { useEffect, useReducer, useRef, useCallback } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import { hotSellerInvoicePeriods, INVOICE_PERIOD_FOR_HISTORICAL_DATA, SELECT_PERIOD } from '../../ARSConstants';
import { formatDateToYYYYMMDD } from 'Common/HelperClass';
import { calculateDateRange, parseDate } from './Helpers';
import PeriodDropdown from './PeriodDropdown';
import DateRangePicker from './DateRangePicker';
import { useDatePickerStyles } from './DatePickerStyles';
import { hotSellerReducer, HotSellerState } from './Reducer';
import { HotSellerPeriodType, TSetPriorityMode, HotSellerInvoicePeriod } from '../../Types';

type HotSellerProps = {
    setModePriority: TSetPriorityMode[];
    updateSetModePriority: (setModePriority: TSetPriorityMode[]) => void;
    onValidationChange?: (isValid: boolean) => void;
};

const HotSeller = ({ setModePriority, updateSetModePriority, onValidationChange }: HotSellerProps) => {
    const hotSellerPriority = setModePriority.find((e) => e.mode === "hot_seller");
    const datePickerCustomStyles = useDatePickerStyles();

    const getHotSellerPeriod = (): HotSellerPeriodType => {
        if (!hotSellerPriority?.invoicePeriod) return 'Last 6 Months';

        if (hotSellerPriority?.mode === 'hot_seller') {
            const hotSellerInvoice = hotSellerPriority.invoicePeriod as HotSellerInvoicePeriod;
            return hotSellerInvoice.selectedPeriod;
        } else {
            const period = hotSellerPriority.invoicePeriod.selectedPeriod;

            if (hotSellerInvoicePeriods.includes(period as any)) {
                return period as HotSellerPeriodType;
            }

            return 'Last 6 Months';
        }
    };

    const initialState: HotSellerState = {
        selectedPeriod: getHotSellerPeriod(),
        isOpen: false,
        selectedFromDate:
            hotSellerPriority?.invoicePeriod?.selectedPeriod === "Custom Date"
                ? parseDate(hotSellerPriority?.invoicePeriod?.startDate)
                : null,
        selectedToDate:
            hotSellerPriority?.invoicePeriod?.selectedPeriod === "Custom Date"
                ? parseDate(hotSellerPriority?.invoicePeriod?.endDate)
                : null
    };

    const [state, dispatch] = useReducer(hotSellerReducer, initialState);

    const stateRef = useRef(state);
    const setModePriorityRef = useRef(setModePriority);

    const validateForm = useCallback((): boolean => {
        const isCustomDateSelected = state.selectedPeriod === 'Custom Date';
        const hasBothDates = Boolean(state.selectedFromDate) && Boolean(state.selectedToDate);
        const isValid = !isCustomDateSelected || (isCustomDateSelected && hasBothDates);

        return isValid;
    }, [state.selectedPeriod, state.selectedFromDate, state.selectedToDate]);

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(validateForm());
        }
    }, [validateForm, onValidationChange]);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        setModePriorityRef.current = setModePriority;
    }, [setModePriority]);

    useEffect(() => {
        stateRef.current = state;
        setModePriorityRef.current = setModePriority;

        if (state.selectedPeriod || state.selectedFromDate || state.selectedToDate) {
            const updatedPriorityModes = [...setModePriority];
            const hotSellerIndex = updatedPriorityModes.findIndex(mode => mode.mode === "hot_seller");

            if (hotSellerIndex !== -1) {
                let { startDate, endDate } = state.selectedPeriod === 'Custom Date'
                    ? {
                        startDate: formatDateToYYYYMMDD(state.selectedFromDate),
                        endDate: formatDateToYYYYMMDD(state.selectedToDate)
                    }
                    : calculateDateRange(state.selectedPeriod);

                const updatedInvoicePeriod: HotSellerInvoicePeriod = {
                    selectedPeriod: state.selectedPeriod,
                    startDate,
                    endDate
                };

                const currentInvoicePeriod = updatedPriorityModes[hotSellerIndex].invoicePeriod as HotSellerInvoicePeriod;
                if (
                    !currentInvoicePeriod ||
                    currentInvoicePeriod.selectedPeriod !== updatedInvoicePeriod.selectedPeriod ||
                    currentInvoicePeriod.startDate !== updatedInvoicePeriod.startDate ||
                    currentInvoicePeriod.endDate !== updatedInvoicePeriod.endDate
                ) {
                    updatedPriorityModes[hotSellerIndex] = {
                        ...updatedPriorityModes[hotSellerIndex],
                        invoicePeriod: updatedInvoicePeriod
                    };
                    updateSetModePriority(updatedPriorityModes);
                }
            }
        }
    }, [state.selectedPeriod, state.selectedFromDate, state.selectedToDate, setModePriority, updateSetModePriority]);

    const handlePeriodSelect = (period: HotSellerPeriodType) => {
        dispatch({ type: 'SET_PERIOD', payload: period });
        dispatch({ type: 'CLOSE_DROPDOWN' });
    };

    return (
        <>
            <style>{datePickerCustomStyles}</style>
            <div className="tw-box-border tw-mt-3 tw-w-auto tw-border-t-[1px] tw-border-solid tw-border-lightgray-200" />
            <div className="tw-p-3">
                <div className="tw-justify-start tw-text-blue-950 tw-text-sm">{INVOICE_PERIOD_FOR_HISTORICAL_DATA}</div>
                <div className="tw-justify-start tw-text-gray-400 tw-text-xs tw-font-semibold tw-pt-4">{SELECT_PERIOD}</div>

                <PeriodDropdown<HotSellerPeriodType>
                    isOpen={state.isOpen}
                    selectedPeriod={state.selectedPeriod}
                    onToggle={() => dispatch({ type: 'TOGGLE_DROPDOWN' })}
                    onSelect={handlePeriodSelect}
                    periods={hotSellerInvoicePeriods}
                />

                {state.selectedPeriod === 'Custom Date' && (
                    <DateRangePicker
                        fromDate={state.selectedFromDate}
                        toDate={state.selectedToDate}
                        onFromDateChange={(date) => dispatch({ type: 'SET_FROM_DATE', payload: date })}
                        onToDateChange={(date) => dispatch({ type: 'SET_TO_DATE', payload: date })}
                        fromDateClassName={!state.selectedFromDate ? "tw-outline-red-500" : ""}
                        toDateClassName={!state.selectedToDate ? "tw-outline-red-500" : ""}
                    />
                )}
            </div>
        </>
    );
};

export default HotSeller;